<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useOperatorApi } from '../composables/useOperatorApi'
import type { MachineStatus } from '../types/operator'

const { t } = useI18n()
const { listMachines } = useOperatorApi()

const machines = ref<MachineStatus[]>([])
const loading = ref(true)
const error = ref('')
const statusFilter = ref('')

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listMachines({
      status: statusFilter.value || undefined,
      page: 1,
      per_page: 100,
    })
    machines.value = result.items
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

watch(statusFilter, load)
onMounted(load)

const filtered = computed(() => machines.value)
const runningCount = computed(() => machines.value.filter((m) => m.status === 'running').length)
const downCount = computed(() => machines.value.filter((m) => m.status === 'down').length)

function statusClass(status: string) {
  return {
    running: 'status-running',
    idle: 'status-idle',
    down: 'status-down',
    setup: 'status-setup',
  }[status] ?? ''
}

function statusLabel(status: string): string {
  if (status === 'running') return t('operator.machines.running')
  if (status === 'idle') return t('operator.machines.idle')
  if (status === 'down') return t('operator.machines.down')
  if (status === 'setup') return t('operator.machines.setup')
  return status
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('operator.machines.eyebrow') }}</p>
      <h2>{{ t('operator.machines.title') }}</h2>
      <p>{{ t('operator.machines.description') }}</p>
    </div>
  </div>

  <!-- Summary + filter -->
  <div class="toolbar">
    <div class="summary" v-if="!loading && !error">
      <span>{{ t('operator.machines.total', { count: machines.length }) }}</span>
      <span class="dot-running">{{ t('operator.machines.runningCount', { count: runningCount }) }}</span>
      <span class="dot-down">{{ t('operator.machines.downCount', { count: downCount }) }}</span>
    </div>
    <select v-model="statusFilter" class="filter-select">
      <option value="">{{ t('operator.machines.allStatuses') }}</option>
      <option value="running">{{ t('operator.machines.running') }}</option>
      <option value="idle">{{ t('operator.machines.idle') }}</option>
      <option value="down">{{ t('operator.machines.down') }}</option>
      <option value="setup">{{ t('operator.machines.setup') }}</option>
    </select>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('operator.machines.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('operator.machines.retry') }}</button>
  </div>

  <!-- Empty -->
  <div v-else-if="filtered.length === 0" class="state-message">
    <p>{{ t('operator.machines.empty') }}</p>
  </div>

  <!-- Table -->
  <table v-else class="data-table">
    <thead>
      <tr>
        <th>{{ t('operator.machines.machine') }}</th>
        <th>{{ t('operator.machines.status') }}</th>
        <th>{{ t('operator.machines.order') }}</th>
        <th>{{ t('operator.machines.lot') }}</th>
        <th>{{ t('operator.machines.counter') }}</th>
        <th>{{ t('operator.machines.fault') }}</th>
        <th>{{ t('operator.machines.actions') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="m in filtered" :key="m.id">
        <td class="cell-name">{{ m.name }}</td>
        <td>
          <span :class="['status-badge', statusClass(m.status)]">{{ statusLabel(m.status) }}</span>
        </td>
        <td class="cell-code">{{ m.current_order_code ?? '—' }}</td>
        <td>{{ m.current_lot_number ?? '—' }}</td>
        <td class="cell-qty">{{ m.counter_shift.toLocaleString() }}</td>
        <td>
          <span v-if="m.fault_active" class="fault-badge">{{ t('operator.machines.activeFault') }}</span>
          <span v-else class="no-fault">{{ t('operator.machines.noFault') }}</span>
        </td>
        <td>
          <RouterLink :to="`/machines/${m.id}`" class="link-action">{{ t('operator.machines.open') }}</RouterLink>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
.page-heading { margin-bottom: 20px; }
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.summary { display: flex; gap: 16px; font-size: 13px; color: #65736e; }
.dot-running::before { content: '● '; color: #1f5c4b; }
.dot-down::before { content: '● '; color: #b33a3a; }
.filter-select {
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  min-width: 160px;
}

.state-message { padding: 40px 0; text-align: center; color: #5f6b6a; }
.state-message.error { color: #b33a3a; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  overflow: hidden;
}
.data-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }
.cell-name { font-weight: 600; }
.cell-code { font-weight: 700; font-family: monospace; }
.cell-qty { font-weight: 700; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: #eaf0ed;
  color: #3d5a4f;
}
.status-running { background: #dcefe7; color: #1f5c4b; }
.status-idle { background: #eaf0ed; color: #65736e; }
.status-down { background: #fde8e8; color: #b33a3a; }
.status-setup { background: #fef3cd; color: #856404; }

.fault-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  background: #fde8e8;
  color: #b33a3a;
}
.no-fault { color: #8b9a95; font-size: 12px; }

.link-action {
  color: #147a8a;
  font-weight: 600;
  font-size: 12px;
  text-decoration: none;
}
.link-action:hover { text-decoration: underline; }

.btn-primary {
  display: inline-block;
  margin-top: 12px;
  padding: 8px 20px;
  background: #147a8a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.btn-primary:hover { background: #0f6573; }
</style>
